import { useEffect, useState } from "react";
import { Card } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import { resourceIcon } from "~/pages/manifold-nextgen/components/resourceIcon";
import { useProjectNeedsSetup } from "~/pages/manifold-nextgen/hooks/useProjectNeedsSetup";
import { NewSessionPicker } from "./NewSessionPicker";
import { ProjectSetupWizard } from "./ProjectSetupWizard";
import { useProjectContext } from "./useProjectContext";

type Mode = "loading" | "setup" | "session";

// Mirrors EmptyConversationView's anchorTop layout for the common case this
// resolves to (an existing project's New Session picker): the guide card's
// collapsed state (identity row + chips) and the composer. Collapsed rather
// than expanded since it's the smaller, more common-looking placeholder.
const LoadingSkeleton = () => (
  <div className="flex flex-1 flex-col items-center gap-8 overflow-y-auto px-8 pb-8 pt-8">
    <Card className="w-full max-w-2xl gap-4 p-0 pb-5">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-5 pt-5">
        <div />
        <div className="flex items-center justify-center gap-3">
          <Skeleton className="size-12 shrink-0 rounded-full" />
          <Skeleton className="h-6 w-40" />
        </div>
        <Skeleton className="size-8 justify-self-end rounded-md" />
      </div>
      <div className="flex flex-wrap gap-2 px-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-32 rounded-full" />
        ))}
      </div>
    </Card>
    <Skeleton className="h-32 w-full max-w-2xl rounded-2xl" />
  </div>
);

export const ProjectIndex = () => {
  const { attachedAgents, attachedDatasets, attachedEntitiesError, isOwner } = useProjectContext();
  const [mode, setMode] = useState<Mode>("loading");

  const isLoading = attachedAgents === undefined || attachedDatasets === undefined;
  const needsSetup = useProjectNeedsSetup(attachedAgents, isOwner);

  // "setup -> session" only happens via the wizard's explicit Start Session
  // button (below) — never automatically, so filling in the last requirement
  // mid-setup doesn't yank the owner into a session before they choose to.
  // "session -> setup" is the opposite: if the project stops being usable
  // (e.g. the last enabled agent is removed from the context sidebar while
  // sitting on this page), fall back to setup immediately, no extra click.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (mode === "loading") {
      setMode(needsSetup ? "setup" : "session");
    } else if (mode === "session" && needsSetup) {
      setMode("setup");
    }
  }, [mode, isLoading, needsSetup]);

  // A failed contents fetch leaves attachedAgents/attachedDatasets undefined,
  // which would otherwise keep the page on the loading skeleton indefinitely.
  // The project itself already loaded (ProjectLayout gates this), so this is
  // scoped to its contents — never "the project doesn't exist".
  if (attachedEntitiesError) {
    return (
      <StatusMessage
        title="Couldn't load this project's contents"
        body="Something went wrong loading its agents and datasets. Try again in a moment."
        icon={resourceIcon("project")}
      />
    );
  }

  if (mode === "loading") {
    return <LoadingSkeleton />;
  }

  if (mode === "setup") {
    return <ProjectSetupWizard onContinue={() => setMode("session")} />;
  }

  return <NewSessionPicker />;
};
